import {Component, inject, computed, model, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatIconModule} from "@angular/material/icon"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MatButtonModule} from "@angular/material/button"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {AuthService} from "@common/services/auth/auth.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {Settings} from "@common/models/settings/settings"
import {ModelState, ModelType, MutationCreateModelInput, NextActor, PaymentState} from "@generated"
import {CreateModelGQL} from "@app/platform/components/models/add-model-dialog/add-model-dialog.generated"

type AddModelData = Partial<MutationCreateModelInput>

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-model-dialog",
    imports: [FormsModule, MatIconModule, MatDialogModule, MatFormFieldModule, MatSelectModule, MatInputModule, OrganizationSelectComponent, MatButtonModule],
    templateUrl: "./add-model-dialog.component.html",
    styleUrl: "./add-model-dialog.component.scss",
})
export class AddModelDialogComponent {
    readonly organizations = inject(OrganizationsService)
    readonly auth = inject(AuthService)
    readonly notifications = inject(NotificationsService)
    readonly createModelGql = inject(CreateModelGQL)
    readonly dialogRef = inject(MatDialogRef<AddModelDialogComponent>)

    readonly $updateSelectedOrganization = signal<{id: string} | undefined | null>(null)
    readonly $selectedOrganization = computed(() => {
        const updateSelectedOrganization = this.$updateSelectedOrganization()
        if (updateSelectedOrganization) return updateSelectedOrganization
        return this.organizations.$current()
    })
    readonly $name = model<string>("", {alias: "name"})

    readonly $data = computed<AddModelData>(() => {
        return {
            name: this.$name(),
            organizationId: this.$selectedOrganization()?.id,
        }
    })

    readonly $canConfirmCreate = computed(() => !!this.$data().name && !!this.$data().organizationId)

    updateOrganization = async (data: {organizationId: string}) => {
        this.$updateSelectedOrganization.set({id: data.organizationId})
    }

    async confirmCreate() {
        const data = this.$data()
        await this.notifications.withUserFeedback(
            async () => {
                const {createModel: model} = await mutateThrowingErrors(this.createModelGql)({
                    input: {
                        ...data,
                        comment: Settings.defaultCommentField,
                        state: ModelState.Draft,
                        paymentState: PaymentState.OrderPlaced,
                        nextActor: this.auth.$actingAsCustomer() ? NextActor.Customer : NextActor.Team1,
                        type: ModelType.Product,
                    },
                })
                // Close dialog with the created model
                this.dialogRef.close(model)
                return model
            },
            {
                success: "Model created.",
                error: "Cannot create model.",
            },
        )
    }
}
